import { z } from "zod";
import { acceptsExactly, DateRangeSchema, InstantRangeSchema, IsoDateSchema, IsoWeekdaySchema, looseUuid, TimeOfDaySchema, } from "@thalamos/common";
export const GetCalendarSyncAuthUrlRequestSchema = z.object({
    providerType: z.enum(["google", "outlook", "stub"]),
});
export const ExchangeCalendarSyncCodeRequestSchema = z.object({
    providerType: z.enum(["google", "outlook", "stub"]),
    code: z.string().min(1),
});
export const ConnectCalendarFeedRequestSchema = z.object({
    providerType: z.literal("ical"),
    feedUrl: z
        .url()
        .refine((url) => url.startsWith("https://") || url.startsWith("webcal://"), "Feed URL must use https:// or webcal://"),
});
export const CreateSection12SpecialismRequestSchema = z.object({
    specialismName: z.string().min(1).max(255),
});
export const UpdateSection12SpecialismRequestSchema = z.object({
    specialismName: z.string().min(1).max(255),
});
/**
 * How long an assessment is reserved for. A product parameter, as the quarter
 * hour itself is: it is both the shortest window an AMHP may request and the
 * free stretch a doctor must have inside one to count as available, because
 * those are the same thing said from either side — the one contiguous stretch
 * the visit occupies.
 */
export const ASSESSMENT_DURATION_MINUTES = 60;
/**
 * How much of a requested window a doctor must be free for to be worth
 * offering to an AMHP who could not be matched outright. A product parameter:
 * short enough that a near miss is still worth a conversation.
 */
export const NEAR_AVAILABILITY_MINUTES = 30;
/**
 * Availability is picked in quarter-hours, so the wire admits no finer time.
 * The constraint lives here rather than on `TimeOfDaySchema` because it
 * describes what a person can offer, not what the model can hold.
 */
const quarterHourTimeOfDay = TimeOfDaySchema.refine((time) => /:(?:00|15|30|45):/.test(time), "Not a quarter of an hour");
const availabilityTimeRangeShape = {
    from: quarterHourTimeOfDay,
    until: quarterHourTimeOfDay,
};
/** Must be total for any pair of strings: Zod runs an object's refinements
 * even when its fields failed. Comparing as text works because a time of day
 * is fixed-width and zero-padded, and puts `"24:00:00"` last where it belongs.
 */
const endsAfterItStarts = (range) => range.until > range.from;
const ENDS_AFTER_IT_STARTS = {
    message: "until must be after from",
    path: ["until"],
};
export const Section12AvailabilityBlockInputSchema = z
    .object(availabilityTimeRangeShape)
    .refine(endsAfterItStarts, ENDS_AFTER_IT_STARTS);
export const Section12AvailabilityWeekdayBlockInputSchema = z
    .object({
    dayOfWeek: IsoWeekdaySchema,
    ...availabilityTimeRangeShape,
})
    .refine(endsAfterItStarts, ENDS_AFTER_IT_STARTS);
/** A block on one calendar date, as availability overrides carry them. */
export const Section12AvailabilityDateBlockSchema = z
    .object({
    date: IsoDateSchema,
    ...availabilityTimeRangeShape,
})
    .refine(endsAfterItStarts, ENDS_AFTER_IT_STARTS);
export const SetSection12AvailabilityBlocksRequestSchema = z.object({
    blocks: z.array(Section12AvailabilityWeekdayBlockInputSchema),
});
export const SetSection12SpecialismsRequestSchema = z.object({
    specialismIds: z.array(z.uuid()),
});
export const SetSection12LocalAuthorityMappingInputSchema = z.object({
    locationId: z.uuid(),
    locationName: z.string().min(1).max(255),
});
export const SetSection12LocalAuthorityMappingsRequestSchema = z.object({
    mappings: z.array(SetSection12LocalAuthorityMappingInputSchema),
});
export const SetSection12AvailabilityOverrideDatesRequestSchema = z
    .object({
    dateRange: DateRangeSchema,
    days: z.array(z.object({
        date: IsoDateSchema,
        blocks: z.array(Section12AvailabilityBlockInputSchema),
    })),
})
    .refine((request) => request.days.every((day) => day.date >= request.dateRange.from &&
    day.date <= request.dateRange.to), { message: "Every day must fall within the range", path: ["days"] })
    .refine((request) => new Set(request.days.map((day) => day.date)).size === request.days.length, { message: "A date may appear only once", path: ["days"] });
export const SendSection12PhoneVerificationCodeRequestSchema = z.object({
    phoneNumber: z
        .string()
        .regex(/^\+[1-9]\d{1,14}$/, "Invalid E.164 phone number format"),
    code: z.string().regex(/^\d{6}$/, "Code must be 6 digits"),
    consent: z.boolean(),
});
export const ConfirmSection12PhoneRequestSchema = z.object({
    phoneNumber: z
        .string()
        .regex(/^\+[1-9]\d{1,14}$/, "Invalid E.164 phone number format"),
    consent: z.boolean(),
});
export const Section12GenderSchema = z.enum([
    "male",
    "female",
    "non-binary",
    "prefer-not-to-say",
]);
export const SECTION_12_GENDER_LABELS = {
    male: "Male",
    female: "Female",
    "non-binary": "Non-binary",
    "prefer-not-to-say": "Prefer not to say",
};
export const SetSection12GenderRequestSchema = z.object({
    gender: Section12GenderSchema.nullable(),
});
export const CreateSection12LanguageRequestSchema = z.object({
    languageName: z.string().min(1).max(255),
});
export const UpdateSection12LanguageRequestSchema = z.object({
    languageName: z.string().min(1).max(255),
});
export const SetSection12LanguagesRequestSchema = z.object({
    languageIds: z
        .array(z.uuid())
        .refine((ids) => new Set(ids).size === ids.length, {
        message: "languageIds must be unique",
    }),
});
export const SetSection12TrustAffiliationRequestSchema = z.object({
    worksForTrust: z.boolean(),
    trustId: z.uuid().nullable(),
    trustName: z.string().max(255).nullable(),
    trustTeam: z.string().max(255).nullable(),
});
export const Section12ExpertiseLevelSchema = z.enum([
    "cct",
    "higher-trainee",
    "experience",
]);
export const SECTION_12_EXPERTISE_LEVEL_LABELS = {
    cct: "CCT",
    "higher-trainee": "Higher Trainee",
    experience: "Experience",
};
export const SetSection12SpecialismWithLevelInputSchema = z.object({
    specialismId: z.uuid(),
    expertiseLevel: Section12ExpertiseLevelSchema.nullable(),
});
export const SetSection12SpecialismsWithLevelsRequestSchema = z.object({
    specialisms: z.array(SetSection12SpecialismWithLevelInputSchema),
});
export const SetSection12MaxTravelTimeRequestSchema = z.object({
    maxTravelTimeMinutes: z.number().int().min(1).max(1440).nullable(),
});
export const SetSection12ContactPreferencesRequestSchema = z.object({
    email: z.boolean(),
    text: z.boolean(),
});
/**
 * The search result as the browser works on it: dates and times of day come
 * back branded, so matching is not handed a string it has to check again.
 * `bookedTimeRanges` stays plain ISO strings, as `InstantRange` carries no
 * brand, so a reader of it still converts.
 */
export const Section12DoctorSearchResultSchema = acceptsExactly()(z.object({
    id: looseUuid(),
    name: z.string(),
    email: z.string(),
    phoneNumber: z.string().nullable(),
    gender: Section12GenderSchema.nullable().optional(),
    languages: z.array(z.object({ id: looseUuid(), languageName: z.string() })),
    worksForTrust: z.boolean().optional(),
    trustId: looseUuid().nullable().optional(),
    trustName: z.string().nullable().optional(),
    trustTeam: z.string().nullable().optional(),
    maxTravelTimeMinutes: SetSection12MaxTravelTimeRequestSchema.shape.maxTravelTimeMinutes.optional(),
    contactPreferenceEmail: z.boolean().optional(),
    contactPreferenceText: z.boolean().optional(),
    specialisms: z.array(z.object({
        id: looseUuid(),
        specialismName: z.string(),
        expertiseLevel: Section12ExpertiseLevelSchema.nullable().optional(),
    })),
    localAuthorities: z.array(z.object({ locationId: looseUuid(), locationName: z.string() })),
    availabilityBlocks: z.array(Section12AvailabilityWeekdayBlockInputSchema),
    availabilityOverrideBlocks: z.array(Section12AvailabilityDateBlockSchema),
    overriddenDates: z.array(IsoDateSchema),
    bookedTimeRanges: z.array(InstantRangeSchema),
}));
/** Maximum number of doctors per route-times request */
export const ROUTE_TIME_MAX_USER_IDS = 20;
export const RouteTimeRequestSchema = z.object({
    formContextId: z.uuid(),
    userIds: z.array(z.uuid()).min(1).max(ROUTE_TIME_MAX_USER_IDS),
});
export const SetHomeBaseLocationRequestSchema = z.object({
    address: z.string().max(500).nullable(),
    postalCode: z.string().max(10).nullable(),
    useProfileAddress: z.boolean().optional(),
});
// ============================================================================
// S12 Payment Requests (Doctor to AMHP workflow)
// ============================================================================
/**
 * Test-only fixture used by integration tests to populate an LA with a known
 * shape of configured rates. Production no longer falls back to defaults —
 * the only journeys are precise (LA-configured rates) and estimated.
 */
export const TEST_S12_PAYMENT_RATES = [
    {
        key: "day-rate",
        label: "Day Rate",
        amountPence: 24000,
    },
    {
        key: "night-rate",
        label: "Night Rate",
        amountPence: 40500,
    },
];
/**
 * Sentinel key and label used for payment request items created in
 * "estimated" mode (i.e. when precise payments are disabled on the
 * AMHP team's organisation). The frontend submits a nominal 0p
 * configured-rate item with this key/label, and the server skips
 * rate-list validation for it.
 */
export const ESTIMATED_RATE_KEY = "estimated";
export const ESTIMATED_RATE_LABEL = "Estimated";
/** The key of the rate an authority pays for a call-out, where no assessment was done. */
export const callOutFeeRateKey = "call-out-fee";
const Section12PaymentRequestItemTypeSchema = z.enum([
    "configured-rate",
    "mileage",
    "other",
]);
export const CreateSection12PaymentRequestReceiptInputSchema = z.object({
    fileName: z
        .string()
        .min(1)
        .transform((name) => {
        // Strip any path components — keep only the final segment
        const basename = name.split(/[\\/]/).pop() ?? name;
        // Replace control characters and characters unsafe in filenames
        const sanitised = basename
            .replace(/[\p{Cc}<>:"|?*]/gu, "_")
            .replace(/_{2,}/g, "_")
            .trim();
        // Truncate to 255 characters
        const truncated = sanitised.slice(0, 255);
        // If nothing useful remains, fall back to a generic name
        return truncated || "receipt";
    }),
    fileType: z.enum(["jpg", "pdf", "png", "webp"]),
    uploadedKey: z
        .string()
        .min(1)
        .max(255)
        .startsWith("user-uploads/", "Invalid upload key"),
});
/**
 * Zod schema targeting {@link Section12PaymentRequestItem}
 * (minus `receipts`, which are joined separately).
 */
export const Section12PaymentRequestItemSchema = z.object({
    id: z.string(),
    paymentRequestId: z.string(),
    itemType: Section12PaymentRequestItemTypeSchema,
    configuredRateKey: z.string().nullable(),
    configuredRateLabel: z.string().nullable(),
    amountPence: z.number(),
    description: z.string().nullable(),
    mileageDistanceMiles: z.nullable(z.coerce.number()),
    startLocation: z.string().nullable(),
    startLocationPostcode: z.string().nullable(),
    created: z.coerce.date().transform((d) => d.toISOString()),
    updated: z.coerce.date().transform((d) => d.toISOString()),
});
/**
 * Maximum allowed fee amount in pence (£1,000,000).
 * Used in both item creation and fee adjustment validation.
 */
export const MAX_FEE_AMOUNT_PENCE = 100000000;
export const CreateSection12PaymentRequestItemInputSchema = z
    .object({
    itemType: Section12PaymentRequestItemTypeSchema,
    configuredRateKey: z.string().min(1).max(100).optional(),
    configuredRateLabel: z.string().min(1).max(255).optional(),
    amountPence: z.number().int().min(0).max(MAX_FEE_AMOUNT_PENCE),
    description: z.string().max(500).optional(),
    mileageDistanceMiles: z.number().min(0).max(10000).optional(),
    startLocation: z.string().max(500).optional(),
    startLocationPostcode: z.string().max(20).optional(),
    receipts: z
        .array(CreateSection12PaymentRequestReceiptInputSchema)
        .max(10)
        .optional(),
})
    .refine((item) => !item.receipts || item.receipts.length === 0 || item.itemType === "other", {
    message: "Receipts can only be attached to an other item",
    path: ["receipts"],
})
    .superRefine((item, ctx) => {
    const hasConfiguredRateFields = item.configuredRateKey !== undefined ||
        item.configuredRateLabel !== undefined;
    if (item.itemType === "other" &&
        (!item.receipts || item.receipts.length === 0)) {
        ctx.addIssue({
            code: "custom",
            message: "Receipts are required for other items",
            path: ["receipts"],
        });
    }
    if (item.itemType === "configured-rate") {
        if (!item.configuredRateKey) {
            ctx.addIssue({
                code: "custom",
                message: "Configured rate key is required",
                path: ["configuredRateKey"],
            });
        }
        if (!item.configuredRateLabel) {
            ctx.addIssue({
                code: "custom",
                message: "Configured rate label is required",
                path: ["configuredRateLabel"],
            });
        }
    }
    if (item.itemType !== "configured-rate" && hasConfiguredRateFields) {
        ctx.addIssue({
            code: "custom",
            message: "Configured rate key and label can only be supplied for configured-rate items",
            path: ["configuredRateKey"],
        });
    }
});
export const ResponsibleLocalAuthoritySourceSchema = z.enum([
    "gp_practice",
    "pds_home_address",
    "emha_address_at_assessment",
    "emha_current_address",
    "assessment_location",
]);
export const ResponsibleLocalAuthorityManualReasonSchema = z.enum([
    "no_pds_link",
    "gp_practice_local_authority_unknown",
    "gp_practice_icb_unknown",
    "gp_practice_local_authority_outside_icb",
    "local_authority_not_configured",
    "outside_england",
    "no_usable_address",
    "lookup_failed",
]);
/**
 * The reasons whose requests eMHA decides again in the background: once the
 * patient is linked to PDS, or once the lookups answer. Until then such a
 * request may still be given an authority by an administrator.
 */
export const responsibleLocalAuthorityManualReasonsDecidedAgain = [
    "no_pds_link",
    "lookup_failed",
];
// A kind of one is its own SnakeCase, which an intersection with it would
// repeat, so the schema is checked against the SnakeCase alone.
export const ResponsibleLocalAuthorityPassedOverAddressKindSchema = z.enum([
    "no_fixed_abode",
]);
export const ResponsibleLocalAuthorityPassedOverGpPracticeKindSchema = z.enum([
    "custodial_setting",
    "outside_england",
]);
/**
 * The five statuses that represent a "payment error" from the doctor's
 * perspective.  Used by the doctor-facing filter and the server to expand the
 * virtual "payment-error" filter value.
 */
export const PAYMENT_ERROR_STATUSES = [
    "payment-on-hold",
    "receipt-missing",
    "bank-information-missing",
    "value-incorrect",
    "registration-with-icb-needed",
];
/**
 * Shared zod enum for the outcome type — reused by the create and update
 * payment request schemas. Kept in sync with `Section12PaymentRequestOutcomeType`
 * (the explicit union type is preserved so tsoa can introspect it for
 * route/swagger generation).
 */
export const Section12PaymentRequestOutcomeTypeSchema = z.enum([
    "form",
    "no-med-rec",
    "completed-outside-thalamos",
    "call-out-fee",
]);
/**
 * The fixed labels a doctor can pick when no medical recommendation is made
 * (`noMedRecReason`). Single source of truth: the frontend dropdown options and
 * the anonymisation allowlist both derive from this. Kept as a readonly tuple so
 * it can seed a `Set`/`z.enum` and yields a literal union type. The "Other" free
 * text is captured separately on the work item, not in this field.
 */
export const NO_MED_REC_REASONS = [
    "No evidence of mental disorder",
    "Legal criteria are not met",
    "Less restrictive treatment option is available",
    "Informal admission",
    "Other",
];
/**
 * The routing columns of a request raised before out-of-area routing existed:
 * every one NULL. Reference data too incomplete to decide still records a
 * scenario, so it is never this.
 */
export const NO_SECTION12_PAYMENT_ROUTING = {
    routingScenario: null,
    invoicingLocalAuthorityId: null,
    invoicingLocalAuthorityName: null,
    invoicingIcbId: null,
    invoicingIcbName: null,
    organisingLocalAuthorityId: null,
    organisingLocalAuthorityName: null,
    organisingIcbId: null,
    organisingIcbName: null,
    responsibleIcbOdsCode: null,
    responsibleIcbName: null,
    responsibleSubIcbLocationOdsCode: null,
};
export const Section12GuestAmhpInviteInputSchema = z.object({
    email: z
        .string()
        .trim()
        .toLowerCase()
        .pipe(z.email("Invalid email address"))
        .pipe(z.string().max(320)),
    fullName: z.string().trim().min(1, "Full name is required").max(255),
});
/**
 * Validates that the items array contains at least one configured rate item.
 */
const hasConfiguredRateItem = (items) => items.some((item) => item.itemType === "configured-rate");
const hasAtMostOneItemOfEachType = (items) => {
    const counts = items.reduce((acc, item) => {
        acc[item.itemType] = (acc[item.itemType] ?? 0) + 1;
        return acc;
    }, {});
    return ["configured-rate", "mileage", "other"].every((itemType) => (counts[itemType] ?? 0) <= 1);
};
export const CreateSection12PaymentRequestInputSchema = z
    .object({
    formContextId: z.uuid(),
    formId: z.uuid().optional(),
    outcomeType: Section12PaymentRequestOutcomeTypeSchema,
    noMedRecReason: z.string().max(1000).optional(),
    amhpTeamId: z.uuid().optional(),
    localAuthorityId: z.uuid().optional(),
    localAuthorityOdsCode: z.string().max(20).optional(),
    localAuthorityName: z.string().max(500).optional(),
    arrangingLocalAuthorityId: z.uuid().optional(),
    arrangingLocalAuthorityName: z.string().max(500).optional(),
    assessmentDateTime: z.iso.datetime().optional(),
    assessmentLocationAddress: z.string().max(500).optional(),
    assessmentLocationPostcode: z.string().max(20).optional(),
    declarationsConfirmed: z.boolean().optional(),
    items: z
        .array(CreateSection12PaymentRequestItemInputSchema)
        .min(1, "At least one item is required"),
    invitedAmhpUserId: z.uuid().optional(),
    guestAmhpInvite: Section12GuestAmhpInviteInputSchema.optional(),
})
    // Exactly one recipient mode: an existing AMHP team, or a guest AMHP invite.
    .refine((data) => (data.amhpTeamId == null) !== (data.guestAmhpInvite == null), {
    message: "Provide either an AMHP team or a guest AMHP invite, not both",
    path: ["amhpTeamId"],
})
    // A guest invite cannot also name a registered AMHP user.
    .refine((data) => !(data.guestAmhpInvite && data.invitedAmhpUserId), {
    message: "invitedAmhpUserId cannot be combined with a guest AMHP invite",
    path: ["invitedAmhpUserId"],
})
    .refine((data) => hasConfiguredRateItem(data.items), {
    message: "At least one configured rate item is required",
    path: ["items"],
})
    .refine((data) => hasAtMostOneItemOfEachType(data.items), {
    message: "Only one configured rate, mileage, and other item may be supplied per request",
    path: ["items"],
})
    .refine((data) => data.outcomeType === "call-out-fee" ||
    (typeof data.assessmentLocationAddress === "string" &&
        data.assessmentLocationAddress.trim().length > 0 &&
        typeof data.assessmentLocationPostcode === "string" &&
        data.assessmentLocationPostcode.trim().length > 0), {
    message: "Assessment location address and postcode are required",
    path: ["assessmentLocationAddress"],
});
export const UpdateSection12PaymentRequestInputSchema = z
    .object({
    amhpTeamId: z.uuid(),
    localAuthorityId: z.uuid().optional(),
    localAuthorityOdsCode: z.string().max(20).optional(),
    localAuthorityName: z.string().max(500).optional(),
    assessmentDateTime: z.iso.datetime().optional(),
    assessmentLocationAddress: z.string().max(500).optional(),
    assessmentLocationPostcode: z.string().max(20).optional(),
    declarationsConfirmed: z.boolean().optional(),
    outcomeType: Section12PaymentRequestOutcomeTypeSchema,
    formId: z.uuid().optional(),
    noMedRecReason: z.string().max(1000).optional(),
    items: z
        .array(CreateSection12PaymentRequestItemInputSchema)
        .min(1, "At least one item is required"),
})
    .refine((data) => hasConfiguredRateItem(data.items), {
    message: "At least one configured rate item is required",
    path: ["items"],
})
    .refine((data) => hasAtMostOneItemOfEachType(data.items), {
    message: "Only one configured rate, mileage, and other item may be supplied per request",
    path: ["items"],
})
    .refine((data) => data.outcomeType === "call-out-fee" ||
    (typeof data.assessmentLocationAddress === "string" &&
        data.assessmentLocationAddress.trim().length > 0 &&
        typeof data.assessmentLocationPostcode === "string" &&
        data.assessmentLocationPostcode.trim().length > 0), {
    message: "Assessment location address and postcode are required",
    path: ["assessmentLocationAddress"],
});
export const LimitedUpdateSection12PaymentRequestInputSchema = z
    .object({
    items: z
        .array(CreateSection12PaymentRequestItemInputSchema)
        .min(1, "At least one item is required"),
})
    .refine((data) => hasConfiguredRateItem(data.items), {
    message: "At least one configured rate item is required",
    path: ["items"],
})
    .refine((data) => hasAtMostOneItemOfEachType(data.items), {
    message: "Only one configured rate, mileage, and other item may be supplied per request",
    path: ["items"],
});
/**
 * Payment request statuses in which the submitting doctor is allowed to
 * edit their own request. Outside these states the request is downstream
 * of the AMHP decision and cannot be edited by the doctor.
 */
export const DOCTOR_EDITABLE_PAYMENT_STATUSES = ["pending", "rejected"];
/**
 * Payment request statuses in which the submitting doctor is allowed to
 * perform a *limited* edit on their own request — the status is preserved
 * and only a status-scoped subset of fields may change. See
 * `LimitedUpdateSection12PaymentRequestInputSchema`.
 *
 * - `value-incorrect`: doctor may amend invoice items (rate / mileage /
 *   other amount + receipts) to resolve the admin's flag.
 * - `receipt-missing`: doctor may only add or remove receipts; item
 *   amounts / descriptions / rates / mileage are immutable.
 *
 * Other request fields (`amhpTeamId`, `localAuthorityId`, `outcomeType`,
 * `formId`, `noMedRecReason`, `assessmentDateTime`) are *not* editable in
 * either limited state.
 */
export const DOCTOR_LIMITED_EDITABLE_PAYMENT_STATUSES = ["value-incorrect", "receipt-missing"];
/**
 * Union of all statuses in which the submitting doctor may open *some*
 * kind of edit UI (either a full edit or a limited edit). Use the
 * narrower constants when the distinction matters (e.g. server-side
 * field allow-lists); use this predicate when the caller only needs to
 * know "should the doctor see an Edit button?".
 */
export const isDoctorEditableStatus = (status) => DOCTOR_EDITABLE_PAYMENT_STATUSES.includes(status) ||
    DOCTOR_LIMITED_EDITABLE_PAYMENT_STATUSES.includes(status);
/**
 * Whether the submitting doctor may open *any* Edit UI for their own request,
 * accounting for guest-backed requests.
 *
 * A guest-AMHP-invite request cannot be fully edited/reassigned: the update
 * path requires an "amhp" team and rejects the guest team. So the full-edit
 * statuses ({@link DOCTOR_EDITABLE_PAYMENT_STATUSES}) are suppressed for guest
 * invites, while limited edits (value-incorrect / receipt-missing → items /
 * receipts only) still work and remain editable. Use this single predicate
 * everywhere the doctor's Edit affordance is gated so the client surfaces stay
 * aligned with the server's update rule.
 */
export const canDoctorEditPaymentRequest = (request) => {
    const isGuestBackedFullEdit = request.isGuestAmhpInvite === true &&
        DOCTOR_EDITABLE_PAYMENT_STATUSES.includes(request.status);
    return isDoctorEditableStatus(request.status) && !isGuestBackedFullEdit;
};
/**
 * Payment request statuses in which the submitting doctor is allowed to
 * soft-delete their own request. Excludes `paid` and `payment-processing`
 * (money has moved or is moving) and the already-`deleted` state.
 */
export const DOCTOR_DELETABLE_PAYMENT_STATUSES = [
    "pending",
    "rejected",
    "accepted",
    "payment-on-hold",
    "receipt-missing",
    "bank-information-missing",
    "value-incorrect",
    "registration-with-icb-needed",
];
export const UpdateSection12PaymentRequestStatusInputSchema = z.object({
    status: z.enum(["accepted", "rejected", "pending"]),
    rejectedReason: z.string().max(1000).optional(),
    amhpFeedback: z
        .object({
        notes: z.string().max(2000).optional(),
        rating: z.number().min(1).max(10).optional(),
    })
        .optional(),
});
export const UpdateSection12PaymentRequestAdminStatusInputSchema = z.object({
    status: z.enum([
        "accepted",
        "paid",
        "payment-processing",
        "payment-on-hold",
        "receipt-missing",
        "bank-information-missing",
        "value-incorrect",
        "registration-with-icb-needed",
    ]),
});
/**
 * The statuses in which a Thalamos Admin is permitted to adjust
 * payment request fee values.
 */
export const ADJUSTABLE_PAYMENT_STATUSES = [
    "accepted",
    "payment-on-hold",
    "receipt-missing",
    "bank-information-missing",
    "value-incorrect",
    "registration-with-icb-needed",
];
/**
 * Maximum character length for a fee adjustment reason.
 */
export const MAX_FEE_ADJUSTMENT_REASON_LENGTH = 2000;
export const AdjustSection12PaymentRequestInputSchema = z
    .object({
    localAuthorityId: z.uuid().nullable().optional(),
    items: z
        .array(z.object({
        itemId: z.uuid(),
        newAmountPence: z
            .number()
            .int()
            .min(0, "Amount must be zero or positive")
            .max(MAX_FEE_AMOUNT_PENCE, "Amount exceeds maximum"),
    }))
        .optional()
        .refine((items) => items === undefined ||
        new Set(items.map((item) => item.itemId)).size === items.length, { message: "Each item can only be adjusted once per request" }),
    reason: z
        .string()
        .trim()
        .min(1, "Reason is required")
        .max(MAX_FEE_ADJUSTMENT_REASON_LENGTH, "Reason must be at most 2000 characters"),
})
    .refine((v) => v.localAuthorityId !== undefined ||
    (v.items !== undefined && v.items.length > 0), {
    message: "Must change at least the local authority or one item amount",
});
//# sourceMappingURL=section12.js.map